import React, { useState, useEffect, useContext } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Progress } from '../../ui/progress';
import { Alert, AlertDescription } from '../../ui/alert';
import { useToast } from '../../ui/use-toast';
import { 
  Users, 
  UserCheck,
  Clock,
  TrendingUp,
  Package,
  AlertTriangle,
  CheckCircle,
  Eye,
  Download,
  BarChart3,
  Grid3x3,
  List,
  MapPin,
  Briefcase
} from 'lucide-react';
import { apiService, ApiTeam, ApiUser, ApiWorkEntry } from '../../../services/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select';
import { exportTeamSummaryToExcel } from '../../../utils/exportUtils';
import { RealTimeContext } from '../../../contexts/RealTimeContext';

interface TeamStats {
  team: ApiTeam;
  supervisor?: ApiUser;
  activeWorkers: number;
  onBreakWorkers: number;
  totalHoursToday: number;
  totalMaterials: number;
  workEntries: ApiWorkEntry[];
  members: ApiUser[];
}

interface TeamsOverviewTabProps {
  monitorId: number;
}

export const TeamsOverviewTab: React.FC<TeamsOverviewTabProps> = ({
  monitorId
}) => {
  const [teams, setTeams] = useState<TeamStats[]>([]);
  const [selectedTeam, setSelectedTeam] = useState<TeamStats | null>(null);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();
  
  // Use context safely without throwing
  const realTimeContext = useContext(RealTimeContext);
  const subscribeToUserEvents = realTimeContext?.subscribeToUserEvents || (() => () => {});

  useEffect(() => {
    loadTeamsData();

    // Listen for refresh events
    const handleRefresh = () => loadTeamsData();
    window.addEventListener('monitor-refresh', handleRefresh);
    
    // Auto refresh every 60 seconds
    const interval = setInterval(loadTeamsData, 60000);
    
    return () => {
      window.removeEventListener('monitor-refresh', handleRefresh);
      clearInterval(interval);
    };
  }, [monitorId, selectedDate]);

  // Subscribe to real-time user status updates
  useEffect(() => {
    if (!realTimeContext) {
      console.warn('RealTime context not available');
      return;
    }
    
    const unsubscribe = subscribeToUserEvents((event: { UserId?: number; userId?: number; IsWorking?: boolean; isWorking?: boolean }) => {
      console.log('TeamsOverviewTab: UserStatusChanged event received', event);
      
      // Update the specific user's status in our teams state
      setTeams(prevTeams => {
        return prevTeams.map(teamStats => {
          const updatedMembers = teamStats.members.map(member => {
            if (member.id === (event.UserId || event.userId)) {
              return {
                ...member,
                isWorking: event.IsWorking ?? event.isWorking ?? false,
                onBreak: event.OnBreak ?? event.onBreak ?? false,
                currentWorkEntryId: event.CurrentWorkEntryId ?? event.currentWorkEntryId,
                currentBreakId: event.CurrentBreakId ?? event.currentBreakId
              };
            }
            return member;
          });
          
          // Recalculate stats
          const activeWorkers = updatedMembers.filter(m => m.isWorking).length;
          const onBreakWorkers = updatedMembers.filter(m => m.onBreak).length;
          
          return {
            ...teamStats,
            members: updatedMembers,
            activeWorkers,
            onBreakWorkers
          };
        });
      });
    });
    
    return () => {
      unsubscribe();
    };
  }, [realTimeContext, subscribeToUserEvents]);

  const loadTeamsData = async () => {
    try {
      setIsLoading(true);
      
      // Get teams monitored by this monitor
      const monitoredTeams = await apiService.getMonitoredTeams(monitorId);
      
      // Get all users and work entries for the selected date
      const [allUsers, workEntriesResponse] = await Promise.all([
        apiService.getAllUsers(),
        apiService.getWorkEntries(selectedDate)
      ]);

      const workEntries = Array.isArray(workEntriesResponse) 
        ? workEntriesResponse 
        : (workEntriesResponse.data || []);

      // Process teams with stats
      const teamsWithStats: TeamStats[] = await Promise.all(
        monitoredTeams.map(async (team) => {
          // Get team members
          const teamMembers = allUsers.filter(u => u.teamId === team.id);
          const supervisor = allUsers.find(u => u.id === team.supervisorId);
          
          // Get work entries for team members
          const teamMemberIds = teamMembers.map(m => m.id);
          const teamWorkEntries = workEntries.filter(entry => 
            teamMemberIds.includes(entry.userId)
          );

          // Calculate stats using new status fields from User model
          const activeWorkers = teamMembers.filter(member => member.isWorking).length;
          const onBreakWorkers = teamMembers.filter(member => member.onBreak).length;

          // Calculate total hours
          const totalHoursToday = teamWorkEntries.reduce((total, entry) => {
            if (entry.startTime) {
              const start = new Date(entry.startTime);
              const end = entry.endTime ? new Date(entry.endTime) : new Date();
              const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
              return total + hours - (entry.totalBreakMinutes || 0) / 60;
            }
            return total;
          }, 0);

          // Count materials (simplified - in real app would query material API)
          const totalMaterials = 0; // TODO: Implement material counting

          return {
            team,
            supervisor,
            activeWorkers,
            onBreakWorkers,
            totalHoursToday,
            totalMaterials,
            workEntries: teamWorkEntries,
            members: teamMembers
          };
        })
      );

      setTeams(teamsWithStats);
    } catch (error) {
      console.error('Error loading teams data:', error);
      toast({
        title: 'Eroare',
        description: 'Nu s-au putut încărca datele echipelor',
        variant: 'destructive'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleExportTeamSummary = async () => {
    try {
      const exportData = teams.flatMap(team => 
        team.members.map(member => {
          const memberEntry = team.workEntries.find(e => e.userId === member.id);
          const isWorking = member.isWorking;
          const onBreak = member.onBreak;
          
          return {
            name: member.name,
            teamName: team.team.name,
            isWorking,
            onBreak,
            todayHours: memberEntry ? calculateHours(memberEntry) : 0
          };
        })
      );

      const filename = `sumar-echipe-${selectedDate}.xlsx`;
      exportTeamSummaryToExcel(exportData, filename);
      
      toast({
        title: 'Export realizat',
        description: 'Sumarul echipelor a fost exportat',
      });
    } catch (error) {
      toast({
        title: 'Eroare',
        description: 'Nu s-a putut exporta sumarul',
        variant: 'destructive'
      });
    }
  };

  const calculateHours = (entry: ApiWorkEntry): number => {
    if (!entry.startTime) return 0;
    const start = new Date(entry.startTime);
    const end = entry.endTime ? new Date(entry.endTime) : new Date();
    const hours = (end.getTime() - start.getTime()) / (1000 * 60 * 60);
    return hours - (entry.totalBreakMinutes || 0) / 60;
  };

  const getTeamHealthColor = (team: TeamStats): string => {
    const workingPercentage = team.members.length > 0 
      ? (team.activeWorkers / team.members.length) * 100 
      : 0;
    
    if (workingPercentage >= 80) return 'text-green-600';
    if (workingPercentage >= 50) return 'text-yellow-600';
    return 'text-red-600';
  };

  const getTeamHealthBadge = (team: TeamStats) => {
    const workingPercentage = team.members.length > 0 
      ? (team.activeWorkers / team.members.length) * 100 
      : 0;
    
    if (workingPercentage >= 80) {
      return <Badge variant="success">Excelent</Badge>;
    }
    if (workingPercentage >= 50) {
      return <Badge variant="warning">Moderat</Badge>;
    }
    return <Badge variant="destructive">Atenție</Badge>;
  };

  // Summary stats
  const totalTeams = teams.length;
  const totalActiveWorkers = teams.reduce((sum, t) => sum + t.activeWorkers, 0);
  const totalWorkers = teams.reduce((sum, t) => sum + t.members.length, 0);
  const totalHours = teams.reduce((sum, t) => sum + t.totalHoursToday, 0);

  return (
    <div className="space-y-6">
      {/* Controls */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Select value={selectedDate} onValueChange={setSelectedDate}>
            <SelectTrigger className="w-48">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[...Array(7)].map((_, i) => {
                const date = new Date();
                date.setDate(date.getDate() - i);
                const dateStr = date.toISOString().split('T')[0];
                return (
                  <SelectItem key={dateStr} value={dateStr}>
                    {date.toLocaleDateString('ro-RO', { 
                      weekday: 'long', 
                      day: 'numeric', 
                      month: 'short' 
                    })}
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>

          <div className="flex items-center gap-2 border rounded-lg p-1">
            <Button
              size="sm"
              variant={viewMode === 'grid' ? 'default' : 'ghost'}
              onClick={() => setViewMode('grid')}
              className="h-8 px-3"
            >
              <Grid3x3 className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant={viewMode === 'list' ? 'default' : 'ghost'}
              onClick={() => setViewMode('list')}
              className="h-8 px-3"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>

        <Button
          onClick={handleExportTeamSummary}
          disabled={isLoading}
          variant="outline"
          className="flex items-center gap-2"
        >
          <Download className="h-4 w-4" />
          Export Excel
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Users className="h-8 w-8 text-blue-500 mb-2" />
              <p className="text-sm text-gray-600">Total Echipe</p>
              <p className="text-2xl font-bold">{totalTeams}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <UserCheck className="h-8 w-8 text-green-500 mb-2" />
              <p className="text-sm text-gray-600">Lucrează Acum</p>
              <p className="text-2xl font-bold">{totalActiveWorkers}/{totalWorkers}</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <Clock className="h-8 w-8 text-purple-500 mb-2" />
              <p className="text-sm text-gray-600">Ore Totale Azi</p>
              <p className="text-2xl font-bold">{totalHours.toFixed(1)}h</p>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col items-center text-center">
              <TrendingUp className="h-8 w-8 text-orange-500 mb-2" />
              <p className="text-sm text-gray-600">Productivitate</p>
              <p className="text-2xl font-bold">
                {totalWorkers > 0 ? Math.round((totalActiveWorkers / totalWorkers) * 100) : 0}%
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Teams Grid/List */}
      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {teams.map((team) => (
            <Card 
              key={team.team.id} 
              className="hover:shadow-lg transition-shadow cursor-pointer"
              onClick={() => setSelectedTeam(team)}
            >
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span className="text-lg">{team.team.name}</span>
                  {getTeamHealthBadge(team)}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Supervisor:</span>
                    <span className="font-medium">{team.supervisor?.name || 'N/A'}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Membri:</span>
                    <span className="font-medium">{team.members.length}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">La lucru:</span>
                    <span className={`font-medium ${getTeamHealthColor(team)}`}>
                      {team.activeWorkers}
                    </span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-600">Ore azi:</span>
                    <span className="font-medium">{team.totalHoursToday.toFixed(1)}h</span>
                  </div>
                </div>

                <div>
                  <div className="flex items-center justify-between text-sm mb-1">
                    <span className="text-gray-600">Activitate</span>
                    <span className="text-gray-600">
                      {team.members.length > 0 
                        ? Math.round((team.activeWorkers / team.members.length) * 100) 
                        : 0}%
                    </span>
                  </div>
                  <Progress 
                    value={team.members.length > 0 
                      ? (team.activeWorkers / team.members.length) * 100 
                      : 0} 
                    className="h-2"
                  />
                </div>

                <Button 
                  variant="outline" 
                  size="sm" 
                  className="w-full flex items-center gap-2"
                  onClick={(e) => {
                    e.stopPropagation();
                    setSelectedTeam(team);
                  }}
                >
                  <Eye className="h-4 w-4" />
                  Vezi Detalii
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4 font-medium text-gray-700">Echipă</th>
                    <th className="text-left p-4 font-medium text-gray-700">Supervisor</th>
                    <th className="text-center p-4 font-medium text-gray-700">Membri</th>
                    <th className="text-center p-4 font-medium text-gray-700">La Lucru</th>
                    <th className="text-center p-4 font-medium text-gray-700">Ore Azi</th>
                    <th className="text-center p-4 font-medium text-gray-700">Status</th>
                    <th className="text-center p-4 font-medium text-gray-700">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {teams.map((team) => (
                    <tr 
                      key={team.team.id} 
                      className="border-b hover:bg-gray-50 cursor-pointer"
                      onClick={() => setSelectedTeam(team)}
                    >
                      <td className="p-4 font-medium">{team.team.name}</td>
                      <td className="p-4">{team.supervisor?.name || 'N/A'}</td>
                      <td className="p-4 text-center">{team.members.length}</td>
                      <td className="p-4 text-center">
                        <span className={getTeamHealthColor(team)}>
                          {team.activeWorkers}
                        </span>
                      </td>
                      <td className="p-4 text-center">{team.totalHoursToday.toFixed(1)}h</td>
                      <td className="p-4 text-center">{getTeamHealthBadge(team)}</td>
                      <td className="p-4 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedTeam(team);
                          }}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {teams.length === 0 && !isLoading && (
        <Card>
          <CardContent className="py-12 text-center">
            <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">Nu există echipe de monitorizat</p>
          </CardContent>
        </Card>
      )}

      {/* Team Details Dialog */}
      {selectedTeam && (
        <Dialog open={!!selectedTeam} onOpenChange={() => setSelectedTeam(null)}>
          <DialogContent className="max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center justify-between">
                <span>{selectedTeam.team.name}</span>
                {getTeamHealthBadge(selectedTeam)}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              {/* Team Info */}
              <div className="grid grid-cols-2 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-600">Supervisor</p>
                    <p className="font-medium">{selectedTeam.supervisor?.name || 'N/A'}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-gray-600">Total Membri</p>
                    <p className="font-medium">{selectedTeam.members.length}</p>
                  </CardContent>
                </Card>
              </div>

              {/* Members List */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-base">Membri Echipă</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 max-h-64 overflow-y-auto">
                    {selectedTeam.members.map((member) => {
                      const memberEntry = selectedTeam.workEntries.find(e => e.userId === member.id);
                      const isWorking = member.isWorking;
                      const onBreak = member.onBreak;
                      
                      return (
                        <div key={member.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                          <div>
                            <p className="font-medium">{member.name}</p>
                            {memberEntry && (
                              <p className="text-sm text-gray-600">
                                <Briefcase className="h-3 w-3 inline mr-1" />
                                {memberEntry.projectName} - 
                                <MapPin className="h-3 w-3 inline mx-1" />
                                {memberEntry.locationName}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            {isWorking ? (
                              onBreak ? (
                                <Badge variant="warning">În pauză</Badge>
                              ) : (
                                <Badge variant="success">La lucru</Badge>
                              )
                            ) : (
                              <Badge variant="secondary">Inactiv</Badge>
                            )}
                            {memberEntry && (
                              <span className="text-sm text-gray-600">
                                {calculateHours(memberEntry).toFixed(1)}h
                              </span>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </CardContent>
              </Card>

              {/* Activity Summary */}
              <Alert>
                <BarChart3 className="h-4 w-4" />
                <AlertDescription>
                  <div className="grid grid-cols-3 gap-4 mt-2">
                    <div>
                      <p className="text-sm text-gray-600">Ore lucrate azi</p>
                      <p className="font-medium">{selectedTeam.totalHoursToday.toFixed(1)}h</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">Productivitate</p>
                      <p className="font-medium">
                        {selectedTeam.members.length > 0 
                          ? Math.round((selectedTeam.activeWorkers / selectedTeam.members.length) * 100) 
                          : 0}%
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600">În pauză</p>
                      <p className="font-medium">{selectedTeam.onBreakWorkers}</p>
                    </div>
                  </div>
                </AlertDescription>
              </Alert>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};